import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

export default function HomeHero() {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 100);
    const t2 = setTimeout(() => setPhase(2), 500);
    const t3 = setTimeout(() => setPhase(3), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <section className="relative min-h-screen flex items-stretch overflow-hidden bg-luxury-dark">
      {/* Cinematic background image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/98633f7821a5dfa8694532b9992d98c1.jpg"
          alt="Premium luxury home interior"
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/97 via-black/90 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 100% at 0% 60%, rgba(3,13,31,0.95) 0%, transparent 55%)' }} />
      </div>

      {/* Blue atmospheric accents */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 45% 60% at 15% 55%, rgba(26,101,224,0.10) 0%, transparent 70%)' }} />

      {/* Vertical accent line */}
      <div className="absolute left-[45%] lg:left-[55%] top-0 bottom-0 w-px hidden xl:block" style={{ background: 'linear-gradient(to bottom, transparent 5%, rgba(26,101,224,0.12) 30%, rgba(26,101,224,0.18) 60%, transparent 95%)' }} />

      {/* Top rule */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, rgba(26,101,224,0.5), rgba(26,101,224,0.2) 50%, transparent)' }} />

      <div className="relative w-full flex items-center pt-28 pb-24 lg:pt-36 lg:pb-32 px-6 lg:px-14">
        <div className="w-full max-w-[1500px] mx-auto grid grid-cols-1 xl:grid-cols-12 gap-10 xl:gap-0 items-center">

          {/* ── LEFT: Main Content ── */}
          <div className="xl:col-span-7 xl:pr-16">

            {/* Eyebrow badge */}
            <div
              className={`flex items-center gap-3 mb-8 transition-all duration-700 ${phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <div className="flex items-center gap-2 border border-white/30 bg-white/10 px-4 py-2 rounded-full">
                <div className="w-1.5 h-1.5 rounded-full bg-luxury-gold animate-pulse" />
                <span className="font-caps text-[10px] tracking-[0.3em] uppercase text-white whitespace-nowrap">
                  Ontario&apos;s Premier HVAC Authority Since 2002
                </span>
              </div>
            </div>

            {/* Main Headline */}
            <h1
              className={`font-serif text-white leading-none mb-6 transition-all duration-900 ${phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '150ms' }}
            >
              <span className="block text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tight text-white">Your Home.</span>
              <span className="block text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-semibold italic text-white" style={{ lineHeight: '0.95' }}>Perfect</span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-white tracking-wide mt-1">Climate, Always.</span>
            </h1>

            {/* Subheadline */}
            <p
              className={`font-sans text-base lg:text-lg text-white leading-relaxed max-w-[520px] mb-10 transition-all duration-700 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              T.S.S.A.-certified specialists delivering flawless heating, cooling, and indoor air quality across Oakville, Burlington, Toronto, and the GTA — with honest pricing and zero pressure.
            </p>

            {/* CTA Buttons */}
            <div
              className={`flex flex-col sm:flex-row gap-4 mb-12 transition-all duration-700 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '100ms' }}
            >
              <a
                href="tel:9058454009"
                className="group inline-flex items-center justify-center gap-3 bg-luxury-gold hover:bg-luxury-gold-light text-white font-caps font-bold text-xs tracking-[0.18em] uppercase rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap shadow-gold"
                style={{ padding: '1.05rem 2.25rem' }}
              >
                <div className="w-5 h-5 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-phone-fill text-sm" />
                </div>
                <span>Call (905) 845-4009</span>
              </a>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-3 border border-white/30 hover:border-luxury-gold/70 text-white hover:text-luxury-gold font-caps text-xs tracking-[0.18em] uppercase px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                <span>Explore Services</span>
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>

            {/* Trust signals */}
            <div
              className={`flex flex-wrap gap-5 transition-all duration-700 ${phase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
              {[
                { icon: 'ri-shield-check-fill', label: 'T.S.S.A. Certified' },
                { icon: 'ri-star-fill', label: '4.9★ HomeStars' },
                { icon: 'ri-time-fill', label: '24/7 Emergency' },
                { icon: 'ri-award-fill', label: 'Est. 2002' },
              ].map((sig) => (
                <div key={sig.label} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${sig.icon} text-luxury-gold text-sm`} />
                  </div>
                  <span className="font-caps text-[10px] tracking-[0.15em] uppercase text-white whitespace-nowrap">{sig.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Contact Card Stack ── */}
          <div
            className={`xl:col-span-5 transition-all duration-1000 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '350ms' }}
          >
            {/* Main contact card */}
            <div
              className="rounded-2xl border border-luxury-gold/20 overflow-hidden"
              style={{ background: 'rgba(3,13,31,0.85)', backdropFilter: 'blur(28px)' }}
            >
              {/* Card header */}
              <div className="border-b border-white/12 px-7 py-4 flex items-center justify-between">
                <span className="font-caps text-[9px] tracking-[0.35em] uppercase text-white">24/7 Service Lines</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="font-caps text-[9px] tracking-wider text-green-200 uppercase">Available Now</span>
                </div>
              </div>

              <div className="p-7 flex flex-col gap-0">
                {/* GTA West */}
                <div className="pb-6">
                  <span className="font-caps text-[9px] tracking-[0.35em] uppercase text-white block mb-2">GTA West &mdash; Oakville &bull; Burlington</span>
                  <a href="tel:9058454009" className="block group cursor-pointer">
                    <span className="font-serif text-luxury-gold text-4xl lg:text-5xl font-semibold group-hover:text-luxury-gold-light transition-colors whitespace-nowrap leading-none">
                      (905) 845-4009
                    </span>
                  </a>
                </div>

                <div className="border-t border-luxury-gold/12 py-6">
                  <span className="font-caps text-[9px] tracking-[0.35em] uppercase text-white block mb-2">GTA East &mdash; Toronto &bull; Etobicoke</span>
                  <a href="tel:4168913444" className="block group cursor-pointer">
                    <span className="font-serif text-luxury-gold text-4xl lg:text-5xl font-semibold group-hover:text-luxury-gold-light transition-colors whitespace-nowrap leading-none">
                      (416) 891-3444
                    </span>
                  </a>
                </div>

                <div className="border-t border-luxury-gold/12 pt-5 pb-7">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg border border-luxury-gold/25 flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-2-line text-luxury-gold/80 text-sm" />
                    </div>
                    <div>
                      <span className="font-caps text-[9px] tracking-[0.2em] uppercase text-white block mb-1">Head Office</span>
                      <p className="font-sans text-sm text-white leading-relaxed">
                        6-1200 Speers Road<br />Oakville, ON L6L 2X4
                      </p>
                    </div>
                  </div>
                </div>

                <a
                  href="tel:9058454009"
                  className="w-full flex items-center justify-center gap-2.5 bg-luxury-gold hover:bg-luxury-gold-light text-white font-caps font-bold text-[11px] tracking-[0.18em] uppercase py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-check-line text-sm" />
                  </div>
                  <span>Book Free In-Home Consultation</span>
                </a>
              </div>
            </div>

            {/* Floating mini stat cards */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { value: '20+', label: 'Yrs Experience', icon: 'ri-award-line' },
                { value: '4.9★', label: 'HomeStars', icon: 'ri-star-fill' },
                { value: '24/7', label: 'Emergency', icon: 'ri-time-line' },
              ].map((stat) => (
                <div
                  key={stat.value}
                  className="rounded-xl border border-luxury-gold/15 py-4 px-3 text-center"
                  style={{ background: 'rgba(3,13,31,0.75)', backdropFilter: 'blur(20px)' }}
                >
                  <div className="w-7 h-7 flex items-center justify-center mx-auto mb-2">
                    <i className={`${stat.icon} text-luxury-gold text-base`} />
                  </div>
                  <div className="font-serif text-luxury-gold text-xl font-bold leading-none mb-1 whitespace-nowrap">{stat.value}</div>
                  <div className="font-caps text-[8px] tracking-[0.15em] uppercase text-white whitespace-nowrap">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <div className="w-[1px] h-12 bg-gradient-to-b from-luxury-gold/40 to-transparent" />
        <span className="font-caps text-[8px] tracking-[0.4em] uppercase text-white/55">Scroll</span>
      </div>
    </section>
  );
}
